import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import "../styles/user-management.css";
import "../styles/company-management.css";
import FormModal from "../components/ui/FormModal";
import ConfirmModal from "../components/ui/ConfirmModal";
import {
  getCompaniesPaginated,
  createCompany,
  patchCompany,
  deleteCompany,
  deleteCompaniesBulk,
} from "../services/companyService";
import { toast } from "../utils/toast";
import { getActionDropdownDirection } from "../utils/dropdownPosition";

const emptyForm = { companyCode: "", name: "", organizationType: "" };

const organizationTypes = [
  { id: 1, label: "Functional" },
  { id: 2, label: "Divisional" },
  { id: 3, label: "Matrix" },
  { id: 4, label: "Flat" },
  { id: 5, label: "Network" },
];

const CompanyManagementPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const lastLoadKeyRef = useRef("");
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [editingId, setEditingId] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    companyCode: "",
    name: "",
    organizationType: "",
  });
  const [editError, setEditError] = useState("");
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm);
  const [createError, setCreateError] = useState("");
  const [searchInput, setSearchInput] = useState(
    () => searchParams.get("search") || "",
  );
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(() => {
    const p = Number(searchParams.get("page") || 1);
    return Number.isInteger(p) && p > 0 ? p : 1;
  });
  const [limit, setLimit] = useState(() => {
    const l = Number(searchParams.get("limit") || 10);
    return Number.isInteger(l) && l > 0 ? l : 10;
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState("down");
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    id: null,
    name: "",
    isBulk: false,
  });

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && !event.target.closest(".actions-menu-wrapper")) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeDropdown]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") setActiveDropdown(null);
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, []);

  const getOrgTypeLabel = (typeId) => {
    const type = organizationTypes.find((t) => t.id === Number(typeId));
    return type ? type.label : "—";
  };

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getCompaniesPaginated({
        page,
        limit,
        search: debouncedSearch,
      });
      setCompanies(Array.isArray(result?.data) ? result.data : []);
      setPagination(
        result?.meta || {
          total: 0,
          page: 1,
          limit: Number(limit) || 10,
          totalPages: 1,
        },
      );
      setError(null);
    } catch (e) {
      setError(e?.message || "Failed to load companies");
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearch]);

  const filteredCompanies = useMemo(() => companies || [], [companies]);

  const allSelected = useMemo(
    () =>
      filteredCompanies.length > 0 &&
      selectedIds.size === filteredCompanies.length,
    [filteredCompanies, selectedIds],
  );

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredCompanies.map((c) => c.company_id)));
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const startEdit = (company) => {
    setEditingId(company.company_id);
    setEditForm({
      companyCode: company.companyCode || "",
      name: company.name || "",
      organizationType: company.org_type_id || "",
    });
    setEditError("");
    setEditOpen(true);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(emptyForm);
    setEditError("");
    setEditOpen(false);
  };

  const saveEdit = async () => {
    const prevId = editingId;
    if (prevId === null || prevId === undefined) {
      setEditError("No company selected for editing");
      return;
    }
    const idNum = Number(prevId);
    if (!Number.isInteger(idNum)) {
      setEditError("Invalid company id");
      return;
    }
    try {
      const payload = {
        companyCode: String(editForm.companyCode || "").trim(),
        name: String(editForm.name || "").trim(),
        org_type_id:
          editForm.organizationType ? Number(editForm.organizationType) : null,
      };
      if (!payload.companyCode) {
        setEditError("Company Code is required");
        return;
      }
      if (payload.companyCode.length < 2 || payload.companyCode.length > 10) {
        setEditError("Company Code must be between 2 and 10 characters!");
        return;
      }
      if (!payload.name) {
        setEditError("Company Name is required");
        return;
      }
      if (payload.name.length < 3 || payload.name.length > 50) {
        setEditError("Company Name must be between 3 and 50 characters!");
        return;
      }
      if (!payload.org_type_id) {
        setEditError("Organization Type is required");
        return;
      }
      await patchCompany(idNum, payload);
      await reload();
      toast.success(`Company "${payload.name}" has been updated successfully!`);
      cancelEdit();
    } catch (e) {
      setEditError(e?.message || "Failed to update company");
    }
  };

  const removeSingle = (id) => {
    const company = companies.find((c) => c.company_id === id);
    const companyName = company?.name || "Company";
    setConfirmDelete({
      open: true,
      id,
      name: companyName,
      isBulk: false,
    });
  };

  const confirmRemoveSingle = async () => {
    const { id, name } = confirmDelete;
    setConfirmDelete({ open: false, id: null, name: "", isBulk: false });

    try {
      await deleteCompany(id);
      await reload();
      setSelectedIds((prev) => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
      toast.success(`Company "${name}" has been deleted successfully!`);
    } catch (e) {
      setError(e?.message || "Failed to delete company");
      toast.error(e?.message || "Failed to delete company");
    }
  };

  const bulkDelete = () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    setConfirmDelete({
      open: true,
      id: null,
      name: "",
      isBulk: true,
      count,
    });
  };

  const confirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    const count = ids.length;
    setConfirmDelete({ open: false, id: null, name: "", isBulk: false });

    try {
      await deleteCompaniesBulk(ids);
      await reload();
      setSelectedIds(new Set());
      toast.success(
        `${count} ${
          count === 1 ? "company" : "companies"
        } deleted successfully!`,
      );
    } catch (e) {
      setError(e?.message || "Failed to delete selected companies");
      toast.error(e?.message || "Failed to delete selected companies");
    }
  };

  const openCreate = () => {
    setCreateForm({ companyCode: "", name: "", organizationType: "" });
    setCreateError("");
    setCreating(true);
  };

  const cancelCreate = () => {
    setCreating(false);
    setCreateForm(emptyForm);
    setCreateError("");
  };

  const submitCreate = async (e) => {
    e?.preventDefault?.();
    const companyCode = String(createForm.companyCode || "").trim();
    const name = String(createForm.name || "").trim();
    const org_type_id =
      createForm.organizationType ? Number(createForm.organizationType) : null;
    if (!companyCode) {
      setCreateError("Company Code is required");
      return;
    }
    if (companyCode.length < 2 || companyCode.length > 10) {
      setCreateError("Company Code must be between 2 and 10 characters!");
      return;
    }
    if (!name) {
      setCreateError("Company Name is required");
      return;
    }
    if (name.length < 3 || name.length > 50) {
      setCreateError("Company Name must be between 3 and 50 characters!");
      return;
    }
    if (!org_type_id) {
      setCreateError("Organization Type is required");
      return;
    }
    try {
      await createCompany({ companyCode, name, org_type_id });
      setPage(1);
      await reload();
      toast.success(`Company "${name}" has been created successfully!`);
      cancelCreate();
    } catch (e) {
      setCreateError(e?.message || "Failed to create company");
    }
  };

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch((searchInput || "").trim());
      setPage(1);
    }, 450);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const pageStr = String(page);
    const limitStr = String(limit);
    const searchStr = String(searchInput || "").trim();

    if (params.get("page") !== pageStr) params.set("page", pageStr);
    if (params.get("limit") !== limitStr) params.set("limit", limitStr);
    if (searchStr) params.set("search", searchStr);
    else params.delete("search");

    const current = searchParams.toString();
    const next = params.toString();
    if (current !== next) {
      setSearchParams(params, { replace: true });
    }
  }, [page, limit, searchInput, searchParams, setSearchParams]);

  useEffect(() => {
    const key = `${page}:${limit}:${debouncedSearch}`;
    if (lastLoadKeyRef.current === key) return;
    lastLoadKeyRef.current = key;
    reload();
  }, [page, limit, debouncedSearch, reload]);

  const totalPages = Math.max(1, Number(pagination.totalPages) || 1);
  const currentPage = Math.min(
    Math.max(1, Number(pagination.page) || page),
    totalPages,
  );
  const totalRows = Number(pagination.total) || 0;
  const pageSize = Number(pagination.limit) || limit;
  const showingFrom = totalRows === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const showingTo =
    totalRows === 0 ? 0 : Math.min(currentPage * pageSize, totalRows);
  const visiblePageItems = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (currentPage <= 2) {
      return [1, 2, 3, "ellipsis", totalPages];
    }

    if (currentPage === 3) {
      return [1, 2, 3, 4, "ellipsis", totalPages];
    }

    if (currentPage === totalPages - 2) {
      return [
        1,
        "ellipsis",
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    if (currentPage >= totalPages - 1) {
      return [1, "ellipsis", totalPages - 2, totalPages - 1, totalPages];
    }

    return [
      1,
      "ellipsis",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "ellipsis",
      totalPages,
    ];
  }, [totalPages, currentPage]);
  const limitOptions = useMemo(() => {
    const base = [5, 10, 20, 50];
    const current = Number(limit);
    const all =
      Number.isInteger(current) && current > 0 ? [...base, current] : base;
    return Array.from(new Set(all)).sort((a, b) => a - b);
  }, [limit]);

  return (
    <>
      <div className="page-container company-page">
        <div className="page-header">
          <div className="header-content">
            <div>
              <h2 className="page-title">Companies</h2>
              <p className="page-subtitle">
                View, create, edit, and delete companies
              </p>
            </div>
            <div className="roles-toolbar">
              <div className="roles-search">
                <input
                  className="search-input"
                  type="text"
                  placeholder="Search by code, name, org type, creator"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                <button
                  className="secondary-btn sm"
                  onClick={() => setSearchInput("")}
                  disabled={loading}
                >
                  Clear
                </button>
              </div>
              <button className="primary-btn" onClick={openCreate}>
                + Create Company
              </button>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="company-pagination-top">
            <label htmlFor="company-limit" className="company-pagination-label">
              Rows per page:
            </label>
            <select
              id="company-limit"
              value={limit}
              onChange={(e) => {
                const next = Number(e.target.value) || 10;
                setLimit(next);
                setPage(1);
              }}
            >
              {limitOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          {selectedIds.size > 0 && (
            <div
              style={{
                marginBottom: "1rem",
                padding: "1rem",
                background: "var(--accent-tint-50)",
                borderRadius: "8px",
                border: "1px solid var(--accent-tint-100)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "0.75rem",
                }}
              >
                <span style={{ fontWeight: "600", color: "var(--accent-ink-800)" }}>
                  {selectedIds.size}{" "}
                  {selectedIds.size === 1 ? "company" : "companies"} selected
                </span>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <button className="danger-btn sm" onClick={bulkDelete}>
                    Delete Selected
                  </button>
                  <button className="secondary-btn sm" onClick={clearSelection}>
                    Clear Selection
                  </button>
                </div>
              </div>
            </div>
          )}
          {error && <div className="error-banner">{error}</div>}
          {loading ?
            <div className="no-results">Loading companies...</div>
          : <div className="roles-table">
              <div className="roles-table-header">
                <div className="cell checkbox">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                  />
                </div>
                <div className="cell name">Company Code</div>
                <div className="cell description">Company Name</div>
                <div className="cell org-type">Organization Type</div>
                <div className="cell created-by">Created by</div>
                <div className="cell actions">Actions</div>
              </div>

              {filteredCompanies.length === 0 ?
                <div className="no-results">No companies found.</div>
              : filteredCompanies.map((c) => (
                  <div
                    className={`roles-table-row ${
                      selectedIds.has(c.company_id) ? "selected" : ""
                    }`}
                    key={c.company_id}
                  >
                    <div className="cell checkbox">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(c.company_id)}
                        onChange={() => toggleSelect(c.company_id)}
                      />
                    </div>

                    <div className="cell name">{c.companyCode}</div>
                    <div className="cell description">{c.name}</div>
                    <div className="cell org-type">
                      {c?.OrganizationType?.name ||
                        getOrgTypeLabel(c.org_type_id)}
                    </div>
                    <div className="cell created-by">
                      {c?.creator?.name || (c.created_by ? "Admin" : "—")}
                    </div>
                    <div className="cell actions">
                      <div
                        className="actions-menu-wrapper"
                        style={{
                          position: "relative",
                          display: "inline-block",
                        }}
                      >
                        <button
                          className="action-menu-btn"
                          aria-haspopup="true"
                          aria-expanded={activeDropdown === c.company_id}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (activeDropdown === c.company_id) {
                              setActiveDropdown(null);
                            } else {
                              setDropdownDirection(
                                getActionDropdownDirection(e.currentTarget),
                              );
                              setActiveDropdown(c.company_id);
                            }
                          }}
                        >
                          Actions ▼
                        </button>
                        {activeDropdown === c.company_id && (
                          <div
                            className={`action-dropdown-menu${
                              dropdownDirection === "up" ? " dropdown-up" : ""
                            }`}
                            role="menu"
                          >
                            <button
                              className="action-menu-item"
                              role="menuitem"
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveDropdown(null);
                                startEdit(c);
                              }}
                            >
                              Edit
                            </button>
                            <button
                              className="action-menu-item danger"
                              role="menuitem"
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveDropdown(null);
                                removeSingle(c.company_id);
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              }
            </div>
          }

          <div className="company-pagination-bottom">
            <div className="company-pagination-summary">
              Showing {showingFrom} to {showingTo} of {totalRows} entries
            </div>
            <div className="company-pagination-actions">
              <button
                className="secondary-btn sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1 || loading}
              >
                Previous
              </button>
              {visiblePageItems.map((item, index) => {
                if (item === "ellipsis") {
                  return (
                    <span
                      key={`ellipsis-${index}`}
                      className="secondary-btn sm"
                      aria-hidden="true"
                    >
                      ...
                    </span>
                  );
                }
                const p = Number(item);
                return (
                  <button
                    key={p}
                    className={
                      p === currentPage ? "primary-btn sm" : "secondary-btn sm"
                    }
                    onClick={() => setPage(p)}
                    disabled={loading}
                  >
                    {p}
                  </button>
                );
              })}
              <button
                className="secondary-btn sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages || loading}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      <FormModal
        open={editOpen}
        title="Edit Company"
        size="compact"
        onCancel={cancelEdit}
        footer={
          <>
            <button type="button" className="primary-btn" onClick={saveEdit}>
              Save
            </button>
            <button
              type="button"
              className="secondary-btn"
              onClick={cancelEdit}
            >
              Cancel
            </button>
          </>
        }
      >
        {editError && (
          <div className="error-banner" style={{ marginBottom: 12 }}>
            {editError}
          </div>
        )}
        <div className="role-card create-card">
          <div className="field">
            <label>Company Code</label>
            <input
              type="text"
              value={editForm.companyCode}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, companyCode: e.target.value }))
              }
              placeholder="e.g., CSP (2-10 characters)"
              required
              maxLength={10}
            />
            <small
              style={{
                color: "var(--gray-500)",
                fontSize: "12px",
                marginTop: "4px",
                display: "block",
              }}
            >
              Must be between 2 and 10 characters
            </small>
          </div>
          <div className="field">
            <label>Company Name</label>
            <input
              type="text"
              value={editForm.name}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, name: e.target.value }))
              }
              placeholder="Company name (3-50 characters)"
              required
              maxLength={50}
            />
            <small
              style={{
                color: "var(--gray-500)",
                fontSize: "12px",
                marginTop: "4px",
                display: "block",
              }}
            >
              Must be between 3 and 50 characters
            </small>
          </div>
          <div className="field">
            <label>Organization Type</label>
            <select
              value={editForm.organizationType}
              onChange={(e) =>
                setEditForm((f) => ({
                  ...f,
                  organizationType: e.target.value,
                }))
              }
              required
            >
              <option value="">Select organization type</option>
              {organizationTypes.map((type) => (
                <option key={type.id} value={type.id} disabled={type.id !== 1}>
                  {type.label}
                  {type.id !== 1 ? " " : ""}
                </option>
              ))}
            </select>
          </div>
        </div>
      </FormModal>

      <FormModal
        open={creating}
        title="Create Company"
        size="compact"
        onCancel={cancelCreate}
        footer={
          <>
            <button
              type="button"
              className="primary-btn"
              onClick={submitCreate}
            >
              Create
            </button>
            <button
              type="button"
              className="secondary-btn"
              onClick={cancelCreate}
            >
              Cancel
            </button>
          </>
        }
      >
        {createError && (
          <div className="error-banner" style={{ marginBottom: 12 }}>
            {createError}
          </div>
        )}
        <div className="role-card create-card">
          <div className="field">
            <label>Company Code</label>
            <input
              type="text"
              value={createForm.companyCode}
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, companyCode: e.target.value }))
              }
              placeholder="e.g., CSP (2-10 characters)"
              required
              maxLength={10}
            />
            <small
              style={{
                color: "var(--gray-500)",
                fontSize: "12px",
                marginTop: "4px",
                display: "block",
              }}
            >
              Must be between 2 and 10 characters
            </small>
          </div>
          <div className="field">
            <label>Company Name</label>
            <input
              type="text"
              value={createForm.name}
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, name: e.target.value }))
              }
              placeholder="Company name (3-50 characters)"
              required
              maxLength={50}
            />
            <small
              style={{
                color: "var(--gray-500)",
                fontSize: "12px",
                marginTop: "4px",
                display: "block",
              }}
            >
              Must be between 3 and 50 characters
            </small>
          </div>
          <div className="field">
            <label>Organization Type</label>
            <select
              value={createForm.organizationType}
              onChange={(e) =>
                setCreateForm((f) => ({
                  ...f,
                  organizationType: e.target.value,
                }))
              }
              required
            >
              <option value="">Select organization type</option>
              {organizationTypes.map((type) => (
                <option key={type.id} value={type.id} disabled={type.id !== 1}>
                  {type.label}
                  {type.id !== 1 ? " " : ""}
                </option>
              ))}
            </select>
          </div>
        </div>
      </FormModal>
      <ConfirmModal
        open={confirmDelete.open}
        title={confirmDelete.isBulk ? "Confirm Bulk Delete" : "Confirm Delete"}
        message={
          confirmDelete.isBulk ?
            `Are you sure you want to delete ${confirmDelete.count} ${
              confirmDelete.count === 1 ? "company" : "companies"
            }? This action cannot be undone.`
          : `Are you sure you want to delete the company "${confirmDelete.name}"? This action cannot be undone.`
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={
          confirmDelete.isBulk ? confirmBulkDelete : confirmRemoveSingle
        }
        onCancel={() =>
          setConfirmDelete({ open: false, id: null, name: "", isBulk: false })
        }
        isDanger={true}
      />
    </>
  );
};

export default CompanyManagementPage;

